/**
 * Capil'liss — comportements de la réplique capilliss.com sur Horizon.
 *
 * 1. <capilliss-filtres-produits> : filtres « Tout / Non classé / Botox / … » de la grille « Nos produits »
 *    (WP : filtres Royal Elementor Addons, animation 0,3 s).
 * 2. Formulaires [data-capilliss-ajout] : lien « Ajouter panier » des cartes, ajout AJAX sans quitter la page
 *    (WP : ajax_add_to_cart WooCommerce, puis lien « Voir le panier »). Émet le CartLinesUpdateEvent standard
 *    pour que l'icône panier, le tiroir panier et le reste de Horizon se mettent à jour nativement.
 *    Sans JavaScript, le formulaire poste vers /cart/add et Shopify redirige vers le panier.
 * 3. [data-capilliss-cart-total] : montant du panier dans l'en-tête, rafraîchi après chaque mise à jour.
 */
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

/* global Theme */
// `Theme` est une constante globale déclarée dans snippets/scripts.liquid (pas une propriété de window).
const routes = () =>
  typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : { cart_add_url: '/cart/add.js', cart_url: '/cart' };

/* 1. Filtres de la grille ------------------------------------------------ */

class CapillissFiltresProduits extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const bouton = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('[data-filtre]') : null
    );
    if (!bouton) return;

    const filtre = bouton.dataset.filtre;
    for (const autre of this.querySelectorAll('[data-filtre]')) {
      autre.setAttribute('aria-pressed', String(autre === bouton));
    }

    for (const item of /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-categorie]'))) {
      const visible = filtre === '*' || item.dataset.categorie === filtre;
      item.hidden = !visible;
      item.classList.toggle('is-filtre-visible', visible);
    }
  };
}

if (!customElements.get('capilliss-filtres-produits')) {
  customElements.define('capilliss-filtres-produits', CapillissFiltresProduits);
}

/* 2. « Ajouter panier » -------------------------------------------------- */

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-capilliss-ajout')) return;

  event.preventDefault();
  ajouterAuPanier(form);
});

/** @param {HTMLFormElement} form */
async function ajouterAuPanier(form) {
  const bouton = /** @type {HTMLButtonElement | null} */ (form.querySelector('[type="submit"]'));
  const voirPanier = /** @type {HTMLElement | null} */ (form.querySelector('[data-capilliss-voir-panier]'));
  if (bouton?.getAttribute('aria-disabled') === 'true') return;

  bouton?.setAttribute('aria-disabled', 'true');
  bouton?.classList.add('is-loading');

  const formData = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));

  const variantId = /** @type {string} */ (formData.get('id'));
  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();

  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variantId, quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(routes().cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    }).then((res) => res.json());

    const cart = await fetch(`${routes().cart_url}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then((res) => res.json());

    if (response.status) {
      form.dispatchEvent(
        new CartErrorEvent({
          error: response.message || 'Add to cart failed',
          code: 'INVALID',
          detail: { description: response.description, errors: response.errors },
        })
      );
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError: true, items: cart.items, source: 'capilliss-card', itemCount: quantity },
      });
      afficherMessage(form, response.description || response.message);
      return;
    }

    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        didError: false,
        items: cart.items,
        source: 'capilliss-card',
        itemCount: quantity,
        productId: form.dataset.productId,
        sections: response.sections,
      },
    });
    if (voirPanier) voirPanier.hidden = false;
  } catch (error) {
    console.error(error);
    deferred.reject(error);
    form.dispatchEvent(
      new CartErrorEvent({ error: error?.message || 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' })
    );
  } finally {
    bouton?.removeAttribute('aria-disabled');
    bouton?.classList.remove('is-loading');
  }
}

/**
 * @param {HTMLFormElement} form
 * @param {string} message
 */
function afficherMessage(form, message) {
  const zone = form.querySelector('[data-capilliss-message]');
  if (!zone || !message) return;
  zone.textContent = message;
  zone.hidden = false;
  setTimeout(() => {
    zone.hidden = true;
  }, 5000);
}

/* 3. Montant du panier dans l'en-tête ------------------------------------ */

async function rafraichirMontantPanier() {
  const cibles = document.querySelectorAll('[data-capilliss-cart-total]');
  if (!cibles.length) return;

  const root = window.Shopify?.routes?.root ?? '/';
  const html = await fetch(`${root}?section_id=capilliss-cart-total`, { credentials: 'same-origin' }).then((res) =>
    res.text()
  );
  const montant = new DOMParser()
    .parseFromString(html, 'text/html')
    .querySelector('[data-capilliss-cart-total-value]')
    ?.textContent?.trim();
  if (!montant) return;

  for (const cible of cibles) cible.textContent = montant;
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {CartLinesUpdateEvent} */ (event).promise;
  (promise ?? Promise.resolve())
    .then(rafraichirMontantPanier)
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[capilliss] montant du panier non rafraîchi :', error);
    });
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) rafraichirMontantPanier();
});
